import is from '@sindresorhus/is';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { type JobItemDetails, useJobItemDetails } from '../api/use-job-item-details';
import { useJobItemVersions } from '../api/use-job-item-versions';
import { useJobArchitectureUrlTileActions } from '../url-tiles/use-job-architecture-url-tile-actions';
import { useDraftJobItemDetails } from './use-draft-job-item-details';

export const useGetActiveJobProfileVersion = (
    itemId: string,
    itemType: 'job-level' | 'job-role',
    architectureId: string,
    initialVersion?: string,
): {
    readonly activeVersion: string | undefined;
    readonly error: Error | null;
    readonly isLoading: boolean;
    readonly item: JobItemDetails | undefined;
    readonly setActiveVersion: (version: string) => void;
} => {
    const { updateJobProfileTileVersion } = useJobArchitectureUrlTileActions();
    const [activeVersion, setActiveVersionInternal] = useState<string | undefined>(initialVersion);
    const [resolvedItemId, setResolvedItemId] = useState<string | null>(null);

    useEffect(() => {
        if (initialVersion !== undefined && initialVersion !== activeVersion) {
            setActiveVersionInternal(initialVersion);
        }
    }, [initialVersion, activeVersion]);
    const { isLoading: isLoadingVersions } = useJobItemVersions({
        architectureId,
        itemId,
        itemType,
    });

    const itemIdChanged = resolvedItemId !== itemId;

    const { draftData, hasDraft } = useDraftJobItemDetails({
        itemId,
    });

    const queryResult = useJobItemDetails({
        architectureId,
        enabled: !itemIdChanged && !hasDraft,
        itemId,
        itemType,
        version: activeVersion,
    });

    const item = useMemo((): JobItemDetails | undefined => {
        if (hasDraft && draftData !== undefined) {
            return draftData;
        }

        return queryResult.data;
    }, [hasDraft, draftData, queryResult.data]);

    const error = useMemo((): Error | null => {
        if (hasDraft) {
            return null;
        }

        return queryResult.error ?? null;
    }, [hasDraft, queryResult.error]);

    const isLoading = useMemo((): boolean => {
        if (itemIdChanged) {
            return true;
        }

        if (hasDraft) {
            return isLoadingVersions;
        }

        return queryResult.isLoading || isLoadingVersions;
    }, [hasDraft, itemIdChanged, queryResult.isLoading, isLoadingVersions]);

    useEffect(() => {
        if (itemIdChanged) {
            setActiveVersionInternal(initialVersion);
            setResolvedItemId(itemId);
        }
    }, [itemId, itemIdChanged, initialVersion]);

    useEffect(() => {
        if (is.nonEmptyString(initialVersion) || is.nonEmptyString(activeVersion)) {
            return;
        }

        const winningVersion = item?.versionInfo?.version;
        if (!is.number(winningVersion) || winningVersion <= 0) {
            return;
        }

        setActiveVersionInternal(winningVersion.toString());
        updateJobProfileTileVersion(winningVersion);
    }, [activeVersion, initialVersion, item?.versionInfo?.version, updateJobProfileTileVersion]);

    const setActiveVersion = useCallback(
        (version: string) => {
            setActiveVersionInternal(version);
            const numericVersion = Number(version);
            if (!Number.isNaN(numericVersion)) {
                updateJobProfileTileVersion(numericVersion);
            }
        },
        [updateJobProfileTileVersion],
    );

    return {
        activeVersion,
        error,
        isLoading,
        item,
        setActiveVersion,
    };
};
